import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Check, X, RefreshCw, Webhook } from "lucide-react";
import { Button } from "@/components/ui/button";

const RESULT_STYLES = {
    "paid": "bg-emerald-50 text-emerald-700",
    "already paid": "bg-sky-50 text-sky-700",
    "failed": "bg-rose-50 text-rose-700",
    "order not found": "bg-amber-50 text-amber-700",
    "invalid signature": "bg-rose-50 text-rose-700",
    "webhook secret not configured": "bg-rose-50 text-rose-700",
    "razor_client not configured": "bg-amber-50 text-amber-700",
    "invalid JSON payload": "bg-rose-50 text-rose-700",
    "unhandled event": "bg-gray-100 text-ink-700",
};

const fmt = (iso) => {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
    } catch {
        return iso;
    }
};

export default function WebhookLogsTab() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const refresh = () => {
        setLoading(true);
        api.get("/admin/webhook-logs?limit=50")
            .then(({ data }) => setRows(data || []))
            .catch(() => setRows([]))
            .finally(() => setLoading(false));
    };
    useEffect(refresh, []);

    return (
        <div data-testid="webhook-logs-tab">
            <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
                <h2 className="font-heading text-2xl font-semibold inline-flex items-center gap-2">
                    <Webhook className="h-5 w-5 text-brand-600" />
                    Razorpay Webhooks
                </h2>
                <Button
                    onClick={refresh}
                    variant="outline"
                    className="rounded-full"
                    data-testid="webhook-logs-refresh-btn"
                >
                    <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
                    Refresh
                </Button>
            </div>

            <p className="text-sm text-ink-700 mb-5 max-w-3xl">
                Latest 50 incoming events from Razorpay (both signature-verified and rejected). Use this when a
                customer says "Razorpay charged me but nothing shows up on my dashboard" — the row will tell you
                whether the event reached this backend, if the signature was valid, which internal order it matched,
                and whether the order actually transitioned to paid.
            </p>

            {rows.length === 0 && !loading ? (
                <div className="rounded-3xl border border-dashed border-gray-200 p-10 text-center text-ink-700 bg-white">
                    No webhook events received yet. Once Razorpay fires an event to
                    <code className="mx-1 px-1.5 py-0.5 bg-paper-alt rounded text-xs">/api/payments/webhook</code>
                    it will show up here.
                </div>
            ) : (
                <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white">
                    <table className="w-full text-sm">
                        <thead className="bg-paper-alt text-ink-700">
                            <tr>
                                <th className="text-left px-4 py-3 font-semibold">Received</th>
                                <th className="text-left px-4 py-3 font-semibold">Event</th>
                                <th className="text-center px-4 py-3 font-semibold">Signature</th>
                                <th className="text-left px-4 py-3 font-semibold">Razorpay Order</th>
                                <th className="text-left px-4 py-3 font-semibold">Internal Order</th>
                                <th className="text-left px-4 py-3 font-semibold">Payment</th>
                                <th className="text-center px-4 py-3 font-semibold">Transitioned</th>
                                <th className="text-left px-4 py-3 font-semibold">Result</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.id} className="border-t border-gray-100" data-testid={`webhook-log-row-${r.id}`}>
                                    <td className="px-4 py-3 text-ink-900 whitespace-nowrap">{fmt(r.received_at)}</td>
                                    <td className="px-4 py-3 text-ink-900 font-medium">{r.event_type || "—"}</td>
                                    <td className="px-4 py-3 text-center">
                                        {r.signature_ok ? (
                                            <span className="inline-flex h-6 w-6 rounded-full bg-emerald-50 text-emerald-700 items-center justify-center" title="Signature verified">
                                                <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
                                            </span>
                                        ) : (
                                            <span className="inline-flex h-6 w-6 rounded-full bg-rose-50 text-rose-700 items-center justify-center" title="Signature failed / not verified">
                                                <X className="h-3.5 w-3.5" strokeWidth={2.5} />
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-ink-700 font-mono text-xs">{r.razorpay_order_id || "—"}</td>
                                    <td className="px-4 py-3 text-ink-700 font-mono text-xs">{r.internal_order_id || "—"}</td>
                                    <td className="px-4 py-3 text-ink-700 font-mono text-xs">{r.razorpay_payment_id || "—"}</td>
                                    <td className="px-4 py-3 text-center">
                                        {r.transitioned ? (
                                            <span className="inline-flex px-2 py-0.5 text-[11px] rounded-full bg-emerald-50 text-emerald-700 font-semibold">yes</span>
                                        ) : (
                                            <span className="inline-flex px-2 py-0.5 text-[11px] rounded-full bg-gray-100 text-ink-700 font-medium">no</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`inline-flex px-2.5 py-1 text-xs rounded-full font-medium ${RESULT_STYLES[r.result] || "bg-gray-100 text-ink-700"}`}>
                                            {r.result}
                                        </span>
                                        {r.http_status !== 200 && (
                                            <span className="ml-2 text-[11px] text-ink-400">HTTP {r.http_status}</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
